import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Branding por tenant en el guest PWA.
 *
 * El header del guest debe mostrar el `name` del tenant (ej. "SAVIs Coffee")
 * cuando el comensal entra por `/r/:slug/...`. Lo resolvemos UNA vez por slug
 * llamando `GET /v1/dhk8-platform-tenants/v1/tenants/by-slug/:slug` (vía
 * `getTenantBySlug` en `lib/api.ts`) y lo cacheamos aquí.
 *
 * Reglas:
 *  - Si NO hay slug → no se hace fetch (el comensal aún no entró por un QR).
 *  - Si la llamada falla → se conserva el último valor o cae al fallback
 *    "NintaiGO" en el componente; el comensal nunca queda sin marca.
 *  - Se persiste para evitar el "flash" de NintaiGO en cada navegación dentro
 *    del mismo restaurante.
 */
export interface TenantBranding {
  slug: string
  tenantId: string
  name: string
  /**
   * Color de acento opcional del tenant — sobreescribe `--brand-accent` en
   * `:root` cuando está presente, sin tocar `--brand` (paleta NintaiGO).
   *
   * Hoy `GET /v1/dhk8-platform-tenants/v1/tenants/by-slug/:slug` NO devuelve
   * este campo; queda plumbing para cuando el endpoint público se extienda.
   * Si es `null/undefined`, el guest usa el verde NintaiGO default. */
  accentColor?: string | null
}

interface BrandingState {
  bySlug: Record<string, TenantBranding>
  /** slug actualmente en pantalla — el header lo lee. */
  activeSlug: string | null
  setBranding: (b: TenantBranding) => void
  setActiveSlug: (slug: string | null) => void
  getBranding: (slug: string | null | undefined) => TenantBranding | null
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set, get) => ({
      bySlug: {},
      activeSlug: null,
      setBranding: (b) => {
        const cur = get().bySlug
        // No reescribimos si nada cambió (evita re-renders).
        const existing = cur[b.slug]
        if (
          existing &&
          existing.name === b.name &&
          existing.tenantId === b.tenantId &&
          (existing.accentColor ?? null) === (b.accentColor ?? null)
        )
          return
        set({ bySlug: { ...cur, [b.slug]: b } })
      },
      setActiveSlug: (slug) => {
        if (get().activeSlug === slug) return
        set({ activeSlug: slug })
      },
      getBranding: (slug) => {
        if (!slug) return null
        return get().bySlug[slug] ?? null
      },
    }),
    { name: 'dhk8-guest-branding' },
  ),
)
